import { useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Brand } from "~/components/brand/Brand";
import { Icon } from "~/components/base/Icon";
import { useT } from "~/hooks/useT";
import { useFocusTrap } from "~/hooks/useFocusTrap";
import { LANGS } from "~/lib/i18nConfig";
import { useUiStore } from "~/stores/uiStore";
import { useAuthStore } from "~/stores/authStore";

interface DrawerItem {
  to: string;
  icon: string;
  labelKey: string;
}

const ITEMS: DrawerItem[] = [
  { to: "/dashboard", icon: "dashboard", labelKey: "dashboard" },
  { to: "/catalog", icon: "catalog", labelKey: "catalog" },
  { to: "/cart", icon: "cart", labelKey: "cart" },
  { to: "/orders", icon: "orders", labelKey: "orders" },
  { to: "/invoices", icon: "invoices", labelKey: "invoices" },
  { to: "/account", icon: "account", labelKey: "account" },
];

export function AppMobileDrawer() {
  const navigate = useNavigate();
  const { t, lang, setLang } = useT();
  const drawerOpen = useUiStore((s) => s.drawerOpen);
  const toggleDrawer = useUiStore((s) => s.toggleDrawer);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const asideRef = useRef<HTMLElement>(null);

  const close = (): void => toggleDrawer();
  useFocusTrap(asideRef, drawerOpen, close);

  if (!drawerOpen) return null;

  const go = (to: string): void => {
    navigate(to);
    close();
  };
  const handleLogout = (): void => {
    logout();
    close();
  };

  return (
    <>
      <button
        type="button"
        aria-label={t("close")}
        className="mobile-sheet-backdrop"
        onClick={close}
        style={{ all: "unset", position: "fixed", inset: 0 }}
      />
      <aside className="mobile-sheet" ref={asideRef} role="dialog" aria-modal="true" aria-label={t("nav_main")}>
        <div
          style={{
            padding: "20px 18px 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid var(--line)",
          }}
        >
          <Brand height={13} withMark showChip />
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={close}
            aria-label={t("close")}
          >
            <Icon name="x" size={16} />
          </button>
        </div>

        <div style={{ padding: 12, flex: 1 }}>
          <div className="nav__section-title">{t("nav_main")}</div>
          {ITEMS.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              onClick={(e) => {
                e.preventDefault();
                go(it.to);
              }}
              className={({ isActive }) =>
                `nav__item${isActive ? " nav__item--active" : ""}`
              }
            >
              <span className="ico">
                <Icon name={it.icon} size={16} />
              </span>
              <span>{t(it.labelKey)}</span>
            </NavLink>
          ))}
        </div>

        <div style={{ borderTop: "1px solid var(--line)", padding: 14 }}>
          {LANGS.length > 1 && (
            <div
              style={{
                display: "inline-flex",
                background: "var(--surface-3)",
                borderRadius: 6,
                padding: 2,
                marginBottom: 12,
                fontFamily: "var(--font-mono)",
                fontSize: 11,
              }}
            >
              {LANGS.map((l) => {
                const active = l === lang;
                return (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setLang(l)}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 4,
                      background: active ? "var(--surface)" : "transparent",
                      color: active ? "var(--ink)" : "var(--ink-3)",
                      fontWeight: active ? 600 : 500,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      boxShadow: active ? "var(--shadow-xs)" : "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
          )}

          <button
            type="button"
            className="nav__user"
            onClick={handleLogout}
            style={{ all: "unset", display: "flex", alignItems: "center", gap: 10, padding: 8, cursor: "pointer", width: "100%" }}
          >
            <div className="nav__avatar">{user?.name?.[0] ?? "?"}</div>
            <div className="nav__user-meta grow truncate">
              <div className="nav__user-name truncate">
                {user?.name ?? t("guest")}
              </div>
              <div className="nav__user-role">{user?.role ?? ""}</div>
            </div>
            <Icon name="logout" size={14} />
          </button>
        </div>
      </aside>
    </>
  );
}
